import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Check, Mail, Calendar, ExternalLink } from "lucide-react";

export default function CTASection() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const onSubmit = (e) => {
    e.preventDefault();
    if (email) { setDone(true); setEmail(""); }
  };

  return (
    <section data-testid="cta-section" ref={ref} id="contact" className="py-28 md:py-40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#C4963B]/[0.07] via-[#121418] to-[#2A7F7A]/[0.04]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#C4963B]/[0.04] rounded-full blur-[140px]" />

      <div className="relative max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C4963B] font-semibold mb-6">Work With Joe</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl lg:text-6xl leading-tight mb-6">
            The Intelligence Age won't wait.
            <br />
            <em className="text-[#C4963B] not-italic">Are you ready to lead it?</em>
          </h2>
          <p className="text-base md:text-lg text-[#E8E6E1]/50 max-w-2xl mx-auto mb-14 leading-relaxed">
            Whether you're looking for a keynote speaker who can move a room of
            5,000 or a trusted advisor to counsel your board on responsible AI
            transformation, let's start the conversation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl mx-auto mb-16">
            <motion.a
              href="https://www.pwc.com/gx/en/about/leadership/joe-atkinson.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-invite-speak-btn"
              className="group flex items-center justify-center gap-3 px-8 py-5 bg-[#C4963B] text-[#121418] text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4A853] transition-colors duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              component={motion.a}
            >
              <Calendar className="w-4 h-4" />
              Invite Joe to Speak
            </motion.a>
            <motion.a
              href="https://www.linkedin.com/in/joe-atkinson-pwc/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-3 px-8 py-5 border border-white/20 text-[#F9F6F0] text-sm uppercase tracking-[0.15em] font-medium hover:border-[#C4963B] hover:text-[#C4963B] transition-colors duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              component={motion.a}
            >
              <ExternalLink className="w-4 h-4" />
              Connect on LinkedIn
            </motion.a>
          </div>

          <p className="text-[11px] text-[#E8E6E1]/25 mb-16">
            Speaking availability is limited. Board advisory inquiries welcome.
          </p>

          <div className="flex items-center gap-6 max-w-xs mx-auto mb-10">
            <div className="flex-1 h-px bg-white/[0.06]" />
            <span className="text-[10px] text-[#E8E6E1]/20 uppercase tracking-[0.25em]">or</span>
            <div className="flex-1 h-px bg-white/[0.06]" />
          </div>

          <div className="max-w-lg mx-auto">
            <p className="text-sm text-[#E8E6E1]/40 mb-5 font-['Cormorant_Garamond'] text-lg italic">
              Get Joe's monthly Board Briefing on AI—clear, actionable, and free.
            </p>
            <form onSubmit={onSubmit}>
              {!done ? (
                <div className="flex flex-col sm:flex-row">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E8E6E1]/20" />
                    <input
                      data-testid="cta-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your work email"
                      className="w-full pl-11 pr-4 py-4 bg-[#1E2229] border border-white/[0.08] border-r-0 text-[#F9F6F0] text-sm placeholder:text-[#E8E6E1]/25 focus:outline-none focus:border-[#C4963B]/40 transition-colors"
                      required
                    />
                  </div>
                  <motion.button
                    data-testid="cta-subscribe-btn"
                    type="submit"
                    className="px-6 py-4 bg-[#C4963B] text-[#121418] text-sm uppercase tracking-[0.1em] font-semibold hover:bg-[#D4A853] transition-colors duration-300 whitespace-nowrap"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Subscribe&nbsp;→
                  </motion.button>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center justify-center gap-2 py-4 px-6 bg-[#2A7F7A]/15 border border-[#2A7F7A]/25 text-[#48A89F] text-sm font-medium"
                >
                  <Check className="w-4 h-4" />
                  You're in. Welcome to the Board Briefing.
                </motion.div>
              )}
              <p className="text-[11px] text-[#E8E6E1]/15 mt-4">
                No spam. Clear-eyed AI insights, delivered monthly. Join 10,000+ executives.
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
